import { useAuth } from "@/_core/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { getLoginUrl } from "@/const";
import { useIsMobile } from "@/hooks/useMobile";
import {
  LayoutDashboard,
  LogOut,
  PanelLeft,
  Building2,
  FileText,
  Settings,
  ChevronRight,
  Import,
  Repeat,
  FileStack,
  ClipboardList,
  FileDown,
  FileBarChart,
  FlaskConical,
  Users,
  Sparkles,
  TrendingUp,
  BarChart3,
  HelpCircle,
  Brain,
  Inbox,
  Network,
  Key,
  Wand2,
  Receipt,
  CircleDollarSign,
  Briefcase,
  CalendarClock,
  Scale,
  Upload,
  UserCircle,
  History,
  Shield,
  Plug,
  Globe,
  Activity,
  Landmark,
} from "lucide-react";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DashboardLayoutSkeleton } from "./DashboardLayoutSkeleton";
import { Button } from "./ui/button";
import { CompanySelector } from "./CompanySelector";
import { LanguageSwitcher } from "@/i18n/LanguageSwitcher";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { setUserContext, clearUserContext } from "@/lib/error-tracking";

/**
 * Roles that have admin-level access to the sidebar Admin menu.
 * Follows the role hierarchy: super_admin > canton_admin > admin > accountant > user
 */
const ADMIN_ROLES = ["admin", "canton_admin", "super_admin"];

/**
 * Navigation Structure - Simplified from 8 to 6 groups
 *
 * Phase 2: Navigation Simplification (Radical Simplicity Release)
 *
 * Design Principles:
 * - Maximum 6 groups for reduced cognitive load
 * - Maximum 6 primary items per group (additional via "More...")
 * - Semantic grouping by user workflow, not feature type
 * - Smart defaults: only active group expanded (Story 2.2)
 *
 * Consolidation:
 * - Overview merged into Dashboard
 * - Documents + Transactions = Transactions (data-centric workflow)
 * - Contacts merged into Entities
 * - Rules & Automation merged into Settings
 * - Financial Analysis merged into Reports
 */
const navigationGroups = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    items: [
      { icon: LayoutDashboard, label: "Overview", path: "/dashboard" },
      {
        icon: CircleDollarSign,
        label: "CFO Dashboard",
        path: "/cfo-dashboard",
      },
      {
        icon: BarChart3,
        label: "Analytics",
        path: "/categorization-analytics",
      },
      { icon: Wand2, label: "Company Discovery", path: "/company-discovery" },
    ],
  },
  {
    label: "Transactions",
    icon: Building2,
    items: [
      { icon: Inbox, label: "Inbox", path: "/file-inbox" },
      { icon: Import, label: "Bank Import", path: "/bank-import" },
      { icon: Building2, label: "All Transactions", path: "/transactions" },
      { icon: Repeat, label: "Reconciliation", path: "/reconciliation" },
      { icon: FileStack, label: "Templates", path: "/templates" },
      {
        icon: Sparkles,
        label: "Categorization Rules",
        path: "/categorization-rules",
      },
    ],
    moreItems: [
      { icon: Upload, label: "Bulk Import", path: "/bulk-import" },
      { icon: Brain, label: "Intelligence", path: "/intelligence" },
      { icon: FileText, label: "All Documents", path: "/documents" },
      { icon: History, label: "Import History", path: "/import-history" },
      {
        icon: TrendingUp,
        label: "Rule Suggestions",
        path: "/rule-suggestions",
      },
      {
        icon: BarChart3,
        label: "Template Analytics",
        path: "/template-analytics",
      },
    ],
  },
  {
    label: "Entities",
    icon: Users,
    items: [
      { icon: Building2, label: "Companies", path: "/companies" },
      { icon: Users, label: "Vendors", path: "/vendors" },
      { icon: UserCircle, label: "Customers", path: "/customers" },
      { icon: Network, label: "Company Settings", path: "/company-settings" },
      { icon: Plug, label: "Integrations", path: "/integrations" },
      { icon: Landmark, label: "Accountants", path: "/accountants" },
    ],
  },
  {
    label: "Tax & Compliance",
    icon: ClipboardList,
    items: [
      { icon: ClipboardList, label: "Tax Reports", path: "/tax" },
      { icon: Receipt, label: "VAT Report", path: "/vat" },
      {
        icon: TrendingUp,
        label: "Tax Optimization",
        path: "/tax-optimization",
      },
      { icon: FlaskConical, label: "R&D Projects", path: "/rnd-projects" },
      { icon: Scale, label: "Swiss GAAP FER", path: "/swiss-gaap-fer" },
      { icon: FileDown, label: "Tax Export", path: "/tax-export" },
      { icon: Globe, label: "Historical Tax", path: "/historical-tax" },
    ],
  },
  {
    label: "Reports",
    icon: FileBarChart,
    items: [
      { icon: FileBarChart, label: "Statements", path: "/statements" },
      { icon: TrendingUp, label: "Financial Ratios", path: "/ratios" },
      {
        icon: CalendarClock,
        label: "Accrual Detector",
        path: "/accrual-detector",
      },
      { icon: Briefcase, label: "Loan Application", path: "/loan-application" },
    ],
  },
  {
    label: "Settings",
    icon: Settings,
    items: [
      { icon: Settings, label: "User Profile", path: "/user-profile" },
      { icon: Brain, label: "AI Settings", path: "/ai-settings" },
      { icon: Activity, label: "AI Usage", path: "/ai-usage" },
      { icon: Key, label: "API Keys", path: "/api-keys" },
      {
        icon: Activity,
        label: "Monitoring",
        path: "/monitoring",
        adminOnly: true,
      },
      { icon: Shield, label: "Admin", path: "/admin", adminOnly: true },
    ],
  },
] as const;

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const DEFAULT_WIDTH = 280;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <div className="flex flex-col items-center gap-6">
            <h1 className="text-2xl font-semibold tracking-tight text-center">
              Sign in to continue
            </h1>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Access to this dashboard requires authentication. Continue to
              launch the login flow.
            </p>
          </div>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl();
            }}
            size="lg"
            className="w-full shadow-lg hover:shadow-xl transition-all"
          >
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
};

function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: DashboardLayoutContentProps) {
  const { user, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Set Sentry user context for error attribution
  useEffect(() => {
    if (user) {
      setUserContext({
        id: user.id,
        email: user.email ?? undefined,
        name: user.name ?? undefined,
      });
    }
    return () => {
      clearUserContext();
    };
  }, [user]);

  /**
   * Story 2.2: Smart Default Navigation States
   *
   * - On page load, only the group containing current route is expanded
   * - All other groups collapsed by default
   * - User preferences saved to localStorage
   */

  // Find active group and item (including moreItems)
  const findActiveGroupAndItem = () => {
    for (const group of navigationGroups) {
      const mainItem = group.items.find(item => item.path === location);
      if (mainItem) {
        return { group, item: mainItem, isMoreItem: false };
      }
      if ("moreItems" in group && group.moreItems) {
        const moreItem = group.moreItems.find(item => item.path === location);
        if (moreItem) {
          return { group, item: moreItem, isMoreItem: true };
        }
      }
    }
    return { group: null, item: null, isMoreItem: false };
  };

  const {
    group: activeGroup,
    item: activeItem,
    isMoreItem,
  } = findActiveGroupAndItem();

  // Track which groups are open (smart defaults: only active group expanded)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const STORAGE_KEY = "taxwiz:navigation-state";
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.expandedGroups) {
          return parsed.expandedGroups;
        }
        return parsed; // Legacy format support
      } catch {
        // Fall through to smart defaults
      }
    }
    // Smart default: only active group expanded
    return navigationGroups.reduce(
      (acc, group) => {
        acc[group.label] = activeGroup?.label === group.label;
        return acc;
      },
      {} as Record<string, boolean>
    );
  });

  // Track which "More..." sections are expanded
  const [openMoreSections, setOpenMoreSections] = useState<
    Record<string, boolean>
  >(() => {
    // Auto-expand if current route is in moreItems
    return navigationGroups.reduce(
      (acc, group) => {
        acc[group.label] = isMoreItem && activeGroup?.label === group.label;
        return acc;
      },
      {} as Record<string, boolean>
    );
  });

  /**
   * Story 2.3: Navigation State Persistence
   *
   * Schema: { expandedGroups: Record<string, boolean>, sidebarCollapsed: boolean }
   * Key: taxwiz:navigation-state
   */
  const STORAGE_KEY = "taxwiz:navigation-state";

  // Save navigation state to localStorage
  useEffect(() => {
    const state = {
      expandedGroups: openGroups,
      sidebarCollapsed: isCollapsed,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [openGroups, isCollapsed]);

  const toggleGroup = (groupLabel: string) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupLabel]: !prev[groupLabel],
    }));
  };

  const toggleMoreSection = (groupLabel: string) => {
    setOpenMoreSections(prev => ({
      ...prev,
      [groupLabel]: !prev[groupLabel],
    }));
  };

  useEffect(() => {
    if (isCollapsed) {
      setIsResizing(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  return (
    <>
      {/* Skip to main content link - a11y */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-background focus:px-4 focus:py-2 focus:rounded-md focus:ring-2 focus:ring-ring focus:shadow-lg"
      >
        Skip to main content
      </a>
      <div className="relative" ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          className="border-r-0"
          disableTransition={isResizing}
        >
          <SidebarHeader className="h-16 justify-center">
            <div className="flex items-center gap-3 px-2 transition-all w-full">
              <button
                onClick={toggleSidebar}
                className="h-8 w-8 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
                aria-label="Toggle navigation"
                aria-expanded={!isCollapsed}
              >
                <PanelLeft
                  className="h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
              </button>
              {!isCollapsed ? (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold tracking-tight truncate">
                    Navigation
                  </span>
                </div>
              ) : null}
            </div>
          </SidebarHeader>

          <SidebarContent className="gap-2">
            {navigationGroups.map(group => {
              const hasMoreItems =
                "moreItems" in group &&
                group.moreItems &&
                group.moreItems.length > 0;
              const isGroupActive = activeGroup?.label === group.label;

              return (
                <Collapsible
                  key={group.label}
                  open={openGroups[group.label]}
                  onOpenChange={() => toggleGroup(group.label)}
                  className="px-2"
                >
                  <CollapsibleTrigger asChild>
                    <button
                      className={`flex items-center justify-between w-full px-2 py-1.5 text-xs font-semibold transition-colors rounded-md hover:bg-accent/50 ${
                        isGroupActive
                          ? "text-primary"
                          : "text-muted-foreground hover:text-foreground"
                      } ${isCollapsed ? "justify-center" : ""}`}
                      aria-expanded={openGroups[group.label]}
                      aria-label={isCollapsed ? group.label : undefined}
                    >
                      {isCollapsed ? (
                        <group.icon className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <>
                          <span>{group.label}</span>
                          <ChevronRight
                            className={`h-4 w-4 transition-transform ${
                              openGroups[group.label] ? "rotate-90" : ""
                            }`}
                            aria-hidden="true"
                          />
                        </>
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-0.5 pt-1">
                    <SidebarMenu>
                      {/* Primary items (max 6) */}
                      {group.items
                        .filter(
                          item =>
                            !("adminOnly" in item && item.adminOnly) ||
                            ADMIN_ROLES.includes(user?.role || "")
                        )
                        .map(item => {
                          const isActive = location === item.path;
                          return (
                            <SidebarMenuItem key={item.path}>
                              <SidebarMenuButton
                                isActive={isActive}
                                onClick={() => setLocation(item.path)}
                                tooltip={item.label}
                                className={`h-9 transition-all font-normal ${
                                  isActive ? "bg-accent" : ""
                                }`}
                              >
                                <item.icon
                                  className={`h-4 w-4 ${isActive ? "text-primary" : ""}`}
                                />
                                <span>{item.label}</span>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          );
                        })}

                      {/* "More..." expandable section */}
                      {hasMoreItems && !isCollapsed && (
                        <Collapsible
                          open={openMoreSections[group.label]}
                          onOpenChange={() => toggleMoreSection(group.label)}
                        >
                          <CollapsibleTrigger asChild>
                            <SidebarMenuItem>
                              <SidebarMenuButton className="h-9 transition-all font-normal text-muted-foreground hover:text-foreground">
                                <ChevronRight
                                  className={`h-4 w-4 transition-transform ${
                                    openMoreSections[group.label]
                                      ? "rotate-90"
                                      : ""
                                  }`}
                                />
                                <span className="text-xs">More...</span>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {(
                                group as (typeof navigationGroups)[1]
                              ).moreItems?.map(item => {
                                const isActive = location === item.path;
                                return (
                                  <SidebarMenuSubItem key={item.path}>
                                    <SidebarMenuSubButton
                                      isActive={isActive}
                                      onClick={() => setLocation(item.path)}
                                      className={`h-8 transition-all ${
                                        isActive ? "bg-accent" : ""
                                      }`}
                                    >
                                      <item.icon
                                        className={`h-3.5 w-3.5 ${isActive ? "text-primary" : ""}`}
                                      />
                                      <span className="text-sm">
                                        {item.label}
                                      </span>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                );
                              })}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </SidebarMenu>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </SidebarContent>

          <SidebarFooter className="p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-9 w-9 border shrink-0">
                    <AvatarFallback className="text-xs font-medium">
                      {user?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                    <p className="text-sm font-medium truncate leading-none">
                      {user?.name || "-"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate mt-1.5">
                      {user?.email || "-"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>
        <div
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[var(--primary)]/20 transition-colors ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => {
            if (isCollapsed) return;
            setIsResizing(true);
          }}
          style={{ zIndex: 50 }}
        />
      </div>

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <span className="tracking-tight text-foreground">
                    {activeItem?.label ?? "Menu"}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <CompanySelector />
            </div>
          </div>
        )}
        {!isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            {/* Breadcrumbs */}
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              {activeGroup && (
                <>
                  <span>{activeGroup.label}</span>
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  <span
                    className="text-foreground font-medium"
                    aria-current="page"
                  >
                    {activeItem?.label}
                  </span>
                </>
              )}
            </nav>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <CompanySelector />
            </div>
          </div>
        )}
        <main id="main-content" className="flex-1 p-4" tabIndex={-1}>
          {children}
        </main>
        {/* Subtle Footer */}
        <footer className="border-t py-3 px-4 text-center text-xs text-muted-foreground/60">
          <span>Swiss Tax Platform v1.1.0</span>
          <span className="mx-2">·</span>
          <span>© {new Date().getFullYear()} Habitusnet</span>
        </footer>
      </SidebarInset>
    </>
  );
}
